/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ReadyState } from "react-use-websocket";
import { getChatHistory } from "../../../services/ChatService";
import {
  convertTimestamp,
  formatDateToISO8601,
} from "../../../utils/allFormatter";
import config from "../../../config";

const SUPPORTED_MEDIA_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
];

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const buildFileViewUrl = (key) => {
  if (!key) return null;
  const base = config.appUrl.endsWith("/")
    ? config.appUrl.slice(0, -1)
    : config.appUrl;
  return `${base}/file/view?key=${key}&type=AUDIO`;
};

const MsgBox = ({
  avatar1,
  avatar2,
  openMsg,
  PerfectScrollbar,
  offMsg,
  lastMessage,
  sendMessage,
  user,
  readyState,
  selectedUser,
  seenHandler,
}) => {
  const state = useSelector((state) => state.msg);
  const roomId = state?.roomId;
  const [conversation, setConversation] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voicePreview, setVoicePreview] = useState(null);
  const [voiceError, setVoiceError] = useState("");
  const [isSendingVoice, setIsSendingVoice] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  const endOfMessagesRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const voicePreviewRef = useRef(null);
  const fetchedRoomIdsRef = useRef(new Set());
  const recordingTimerRef = useRef(null);
  const outgoingQueueRef = useRef([]); // queued payloads when socket isn't open
  const lastFetchedTimeRef = useRef(0);
  const pollingIntervalRef = useRef(null);

  const clearVoicePreview = useCallback(() => {
    if (voicePreviewRef.current?.url) {
      URL.revokeObjectURL(voicePreviewRef.current.url);
    }
    voicePreviewRef.current = null;
    setVoicePreview(null);
  }, []);

  const updateVoicePreview = useCallback((preview) => {
    if (voicePreviewRef.current?.url) {
      URL.revokeObjectURL(voicePreviewRef.current.url);
    }
    voicePreviewRef.current = preview;
    setVoicePreview(preview);
  }, []);

  const stopStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const stopRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingTime(0);
  }, []);

  const teardownMedia = useCallback(
    (shouldResetPreview = false) => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      stopStream();
      stopRecordingTimer();

      if (shouldResetPreview) {
        clearVoicePreview();
      } else if (voicePreviewRef.current?.url) {
        URL.revokeObjectURL(voicePreviewRef.current.url);
        voicePreviewRef.current = null;
      }
    },
    [clearVoicePreview, stopStream, stopRecordingTimer]
  );

  const cancelVoiceMode = useCallback(() => {
    teardownMedia(true);
    setIsRecording(false);
    setIsVoiceMode(false);
    setVoiceError("");
    setIsSendingVoice(false);
  }, [teardownMedia]);

  const dispatchMessagePayload = useCallback(
    (payload, conversationItem) => {
      // attach a client-generated id for reconciliation
      const clientId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const payloadWithId = {
        ...payload,
        payload: { ...payload.payload, clientid: clientId },
      };
      const optimisticItem = {
        ...conversationItem,
        clientid: clientId,
        status: "pending",
      };

      // optimistic append to UI
      setConversation((prev) => [...prev, optimisticItem]);

      if (readyState === 1) {
        sendMessage(JSON.stringify(payloadWithId));
      } else {
        // queue until socket opens
        outgoingQueueRef.current.push(payloadWithId);
      }

      // Fallback: if no echo arrives, mark as sent after a grace period
      setTimeout(() => {
        setConversation((prev) => {
          const idx = prev.findIndex(
            (m) => m.clientid === clientId && m.status === "pending"
          );
          if (idx === -1) return prev;
          const next = [...prev];
          next[idx] = { ...next[idx], status: "sent" };
          return next;
        });
      }, 6000);
    },
    [readyState, sendMessage]
  );

  useEffect(() => {
    // flush queued messages when socket opens
    if (readyState === 1 && outgoingQueueRef.current.length > 0) {
      const queue = [...outgoingQueueRef.current];
      outgoingQueueRef.current = [];
      queue.forEach((msg) => {
        try {
          sendMessage(JSON.stringify(msg));
        } catch (e) {
          // if send fails, requeue
          outgoingQueueRef.current.push(msg);
        }
      });
    }
  }, [readyState, sendMessage]);

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startVoiceRecording = useCallback(async () => {
    setVoiceError("");
    setIsSendingVoice(false);

    const canRecord =
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      typeof window.MediaRecorder !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function";

    if (!canRecord) {
      setIsVoiceMode(true);
      setIsRecording(false);
      setVoiceError("Voice recording is not supported in this browser.");
      teardownMedia(true);
      return;
    }

    teardownMedia(true);
    setIsVoiceMode(true);
    setRecordingTime(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mimeType =
        typeof window.MediaRecorder.isTypeSupported === "function"
          ? SUPPORTED_MEDIA_TYPES.find((type) =>
              window.MediaRecorder.isTypeSupported(type)
            )
          : "";

      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new window.MediaRecorder(stream, recorderOptions);
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stopStream();
        stopRecordingTimer();

        const recorderMimeType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, {
          type: recorderMimeType,
        });
        audioChunksRef.current = [];

        const url = URL.createObjectURL(blob);
        const preview = {
          blob,
          url,
          name: `voice-${Date.now()}.webm`,
          mimeType: recorderMimeType,
        };
        updateVoicePreview(preview);
        mediaRecorderRef.current = null;
        setIsRecording(false);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.log(error);
      teardownMedia(true);
      setIsRecording(false);
      setVoiceError("Unable to access microphone. Please check permissions.");
    }
  }, [stopStream, teardownMedia, updateVoicePreview, stopRecordingTimer]);

  const stopVoiceRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const sendMessageHandler = useCallback(() => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      return;
    }

    const tempMessage = {
      type: "send_message",
      payload: {
        message: trimmedMessage,
        roomid: roomId,
      },
    };

    const conversationItem = {
      message: trimmedMessage,
      roomid: roomId,
      senderid: user?.loggedinprofileid,
      isseen: false,
      createdat: formatDateToISO8601(),
    };

    dispatchMessagePayload(tempMessage, conversationItem);
    setMessage("");
  }, [
    dispatchMessagePayload,
    message,
    roomId,
    user?.loggedinprofileid,
    user?.tenant?.id,
  ]);

  const sendVoiceMessageHandler = useCallback(async () => {
    const preview = voicePreviewRef.current;

    if (!preview?.blob) {
      setVoiceError("Voice recording is empty. Please record again.");
      return;
    }

    setIsSendingVoice(true);
    setVoiceError("");

    try {
      const dataUrl = await blobToDataUrl(preview.blob);
      console.log(dataUrl);
      const fileName = preview.name || `voice-${Date.now()}.webm`;
      const contentType = preview.mimeType || preview.blob.type || "audio/webm";

      const tempMessage = {
        type: "send_message",
        payload: {
          message: "",
          roomid: roomId,
          voice: {
            name: fileName,
            content_type: contentType,
            data: dataUrl,
          },
        },
      };

      const conversationItem = {
        message: "",
        voice: {
          name: fileName,
          content_type: contentType,
          data: dataUrl,
        },
        roomid: roomId,
        senderid: user?.loggedinprofileid,
        isseen: false,
        createdat: formatDateToISO8601(),
      };

      // Send the message payload through the socket
      dispatchMessagePayload(tempMessage, conversationItem);
      cancelVoiceMode();
    } catch (error) {
      console.log(error);
      setVoiceError("Failed to send voice message. Please try again.");
      setIsSendingVoice(false);
    }
  }, [
    cancelVoiceMode,
    dispatchMessagePayload,
    roomId,
    user?.loggedinprofileid,
    user?.tenant?.id,
  ]);

  const resolveVoiceSource = useCallback((item) => {
    const audioFromAllFiles = Array.isArray(item?.allfiles)
      ? item.allfiles.find(
          (file) =>
            String(file?.type || "").toLowerCase() === "audio" ||
            String(file?.content_type || "")
              .toLowerCase()
              .includes("audio")
        )
      : null;

    if (audioFromAllFiles) {
      if (audioFromAllFiles.url) {
        return audioFromAllFiles.url;
      }
      if (
        audioFromAllFiles.data &&
        audioFromAllFiles.data.startsWith("data:")
      ) {
        return audioFromAllFiles.data;
      }
      if (audioFromAllFiles.key) {
        return buildFileViewUrl(audioFromAllFiles.key);
      }
    }

    const voice = item?.voice;

    if (voice) {
      if (typeof voice === "string") {
        if (voice.startsWith("data:")) {
          return voice;
        }
        return buildFileViewUrl(voice);
      }

      if (voice.data && voice.data.startsWith("data:")) {
        return voice.data;
      }

      if (voice.dataUrl) {
        return voice.dataUrl;
      }

      if (voice.localUrl) {
        return voice.localUrl;
      }

      if (voice.url) {
        return voice.url;
      }

      const keyCandidate =
        voice.key || voice.voicekey || voice.voiceKey || voice.storage_key;

      if (keyCandidate) {
        return buildFileViewUrl(keyCandidate);
      }
    }

    const voiceKey =
      item?.voicekey || item?.voiceKey || item?.voice_url || item?.voiceUrl;

    if (voiceKey) {
      if (String(voiceKey).startsWith("data:")) {
        return voiceKey;
      }
      return buildFileViewUrl(voiceKey);
    }

    return null;
  }, []);

  const renderMessageContent = (item) => {
    const voiceSource = resolveVoiceSource(item);

    if (voiceSource) {
      const voice = item?.voice;
      const voiceName =
        (voice && voice.name) ||
        item?.voice_name ||
        item?.voicename ||
        (typeof voice === "string" ? voice.split("/").pop() : "") ||
        "Voice note";

      const downloadKey =
        (voice && (voice.key || voice.voicekey || voice.voiceKey)) ||
        item?.voicekey ||
        item?.voiceKey;

      const downloadUrl = downloadKey
        ? buildFileViewUrl(downloadKey)
        : voiceSource;

      return (
        <div
          className="d-flex align-items-center gap-2 p-2 rounded"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            minWidth: "220px",
            maxWidth: "280px",
          }}
        >
          <div
            className="voice-icon d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "32px",
              height: "32px",
              background: "rgba(255, 255, 255, 0.2)",
              flexShrink: 0,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="white"
              viewBox="0 0 16 16"
            >
              <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z" />
              <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z" />
            </svg>
          </div>
          <div className="flex-grow-1">
            <audio
              controls
              src={voiceSource}
              style={{
                width: "100%",
                height: "32px",
                background: "",
              }}
            >
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      );
    }

    return item?.message;
  };

  const fetchChatHistory = useCallback(() => {
    if (!roomId) {
      setConversation([]);
      setLoading(false);
      return;
    }

    // Prevent duplicate API calls for the same roomId
    if (fetchedRoomIdsRef.current.has(roomId)) {
      return;
    }

    setLoading(true);
    fetchedRoomIdsRef.current.add(roomId);

    getChatHistory(roomId)
      .then((res) => {
        const response = res.data?.result;
        if (Array.isArray(response)) {
          response.sort(
            (a, b) => new Date(a.createdat) - new Date(b.createdat)
          );
          const normalized = response.map((item) => {
            let result = item;
            if (!result?.voice && result?.voicekey) {
              result = { ...result, voice: result.voicekey };
            }
            if (!result?.voice && result?.voiceKey) {
              result = { ...result, voice: result.voiceKey };
            }
            if (
              !result?.voice &&
              Array.isArray(result?.allfiles) &&
              result.allfiles.some(
                (file) =>
                  String(file?.type || "").toLowerCase() === "audio" ||
                  String(file?.content_type || "")
                    .toLowerCase()
                    .includes("audio")
              )
            ) {
              const audioFile = result.allfiles.find(
                (file) =>
                  String(file?.type || "").toLowerCase() === "audio" ||
                  String(file?.content_type || "")
                    .toLowerCase()
                    .includes("audio")
              );
              result = { ...result, voice: audioFile };
            }
            // Ensure previously loaded messages render as sent (blue check)
            return { ...result, status: "sent" };
          });
          setConversation(normalized);
        } else {
          setConversation([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        // Remove from fetched set on error so retry is possible
        fetchedRoomIdsRef.current.delete(roomId);
      });
  }, [roomId]);

  // Polling fallback: if websocket isn't receiving messages, poll the API
  useEffect(() => {
    if (!roomId) {
      return;
    }

    const POLL_INTERVAL = 30000; // 30 seconds

    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        const now = Date.now();
        // Only poll if we haven't received a message in the last 30 seconds
        if (now - lastFetchedTimeRef.current > POLL_INTERVAL) {
          // Re-fetch chat history to sync missed messages
          fetchedRoomIdsRef.current.delete(roomId);
          fetchChatHistory();
        }
      }, POLL_INTERVAL);
    };

    // Start polling if websocket is closed or connecting
    if (readyState !== 1) {
      startPolling();
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [roomId, readyState, fetchChatHistory]);

  useEffect(() => {
    if (lastMessage === null) {
      return;
    }

    try {
      // Update last fetched time when receiving a message
      lastFetchedTimeRef.current = Date.now();

      const updateMessage = JSON.parse(lastMessage.data);
      const payload = updateMessage?.payload ?? {};
      const incoming = {
        message: payload?.message ?? "",
        voice: payload?.voice,
        voicekey: payload?.voicekey ?? payload?.voiceKey ?? payload?.voice?.key,
        roomid: payload?.roomId ?? payload?.roomid ?? roomId,
        senderid:
          payload?.from?.profileid ??
          payload?.senderid ??
          updateMessage?.from?.profileid,
        createdat: formatDateToISO8601(),
        clientid: payload?.clientid,
        status: "sent",
      };

      setConversation((prev) => {
        // if this is an echo of a pending optimistic message, reconcile
        if (incoming.clientid) {
          const idx = prev.findIndex((m) => m.clientid === incoming.clientid);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = { ...next[idx], status: "sent" };
            return next;
          }
        }

        // Fallback: if it's from me and same text and recent, mark last pending as sent
        const myId = user?.loggedinprofileid;
        if (incoming.senderid === myId && incoming.message) {
          for (let i = prev.length - 1; i >= 0; i--) {
            const m = prev[i];
            if (m.status === "pending" && m.message === incoming.message) {
              const next = [...prev];
              next[i] = { ...m, status: "sent" };
              return next;
            }
          }
        }

        // Fallback for voice: match first pending voice in this room without voicekey
        if (incoming.senderid === myId && incoming.voicekey) {
          for (let i = 0; i < prev.length; i++) {
            const m = prev[i];
            const isSameRoom = m.roomid === incoming.roomid;
            const isPendingVoice =
              m.status === "pending" && m.voice && !m.voicekey;
            if (isSameRoom && isPendingVoice) {
              const next = [...prev];
              next[i] = { ...m, status: "sent", voicekey: incoming.voicekey };
              return next;
            }
          }
        }

        // otherwise append as a new message
        return [...prev, incoming];
      });
    } catch (error) {
      console.log("Failed to parse incoming message", error);
    }
  }, [lastMessage, roomId, user?.loggedinprofileid]);

  useEffect(() => {
    if (!roomId) {
      return;
    }
    fetchChatHistory();
    seenHandler(roomId);
  }, [roomId]);

  useEffect(() => {
    cancelVoiceMode();
  }, [roomId]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    const container = document.getElementById("DZ_W_Contacts_Body3");
    if (!container) return;
    // Defer to next frame so DOM updates are applied
    window.requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  };

  useEffect(() => {
    return () => {
      teardownMedia(false);
    };
  }, [teardownMedia]);

  return (
    <div className={`card chat dz-chat-history-box ${openMsg ? "" : "d-none"}`}>
      <div className="justify-content-start card-header chat-list-header text-center">
        <Link
          to={"#"}
          className="dz-chat-history-back"
          onClick={() => {
            setMessage("");
            cancelVoiceMode();
            offMsg();
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            width="18px"
            height="18px"
            viewBox="0 0 24 24"
            version="1.1"
          >
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <polygon points="0 0 24 0 24 24 0 24" />
              <rect
                fill="#000000"
                opacity="0.3"
                transform="translate(15.000000, 12.000000) scale(-1, 1) rotate(-90.000000) translate(-15.000000, -12.000000) "
                x="14"
                y="7"
                width="2"
                height="10"
                rx="1"
              />
              <path
                d="M3.7071045,15.7071045 C3.3165802,16.0976288 2.68341522,16.0976288 2.29289093,15.7071045 C1.90236664,15.3165802 1.90236664,14.6834152 2.29289093,14.2928909 L8.29289093,8.29289093 C8.67146987,7.914312 9.28105631,7.90106637 9.67572234,8.26284357 L15.6757223,13.7628436 C16.0828413,14.136036 16.1103443,14.7686034 15.7371519,15.1757223 C15.3639594,15.5828413 14.7313921,15.6103443 14.3242731,15.2371519 L9.03007346,10.3841355 L3.7071045,15.7071045 Z"
                fill="#000000"
                fillRule="nonzero"
                transform="translate(9.000001, 11.999997) scale(-1, -1) rotate(90.000000) translate(-9.000001, -11.999997) "
              />
            </g>
          </svg>
        </Link>

        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1">
          <h6 className="mb-1">Chat with {selectedUser?.name}</h6>
        </div>
      </div>

      <PerfectScrollbar
        className={`card-body msg_card_body dz-scroll ${
          openMsg ? "ps ps--active-y" : ""
        } `}
        id="DZ_W_Contacts_Body3"
        style={{
          maxHeight: isVoiceMode
            ? "calc(100vh - 350px)"
            : "calc(100vh - 240px)",
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div className="d-flex justify-content-center mt-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div ref={endOfMessagesRef}>
            {conversation?.map((item, index) => (
              <div key={index}>
                {user?.loggedinprofileid === item.senderid ? (
                  <div className="d-flex justify-content-end mb-2">
                    <div
                      className={`msg_cotainer_send ${
                        item.status === "pending" ? "pending" : ""
                      }`}
                    >
                      {renderMessageContent(item)}
                      <br />
                      <span className="msg_time_send d-inline-flex align-items-center">
                        {convertTimestamp(item?.createdat)}
                        <span
                          className={`msg_status_icon ${
                            item.status === "sent" ? "sent" : "pending"
                          }`}
                          title={item.status === "sent" ? "Sent" : "Sending"}
                        >
                          <i className="fa fa-check" aria-hidden="true"></i>
                        </span>
                      </span>
                    </div>
                    <div className="img_cont_msg ms-2">
                      <img
                        src={selectedUser?.photo || avatar1}
                        className="rounded-circle user_img_msg"
                        alt=""
                      />
                    </div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-start mb-2">
                    <div className="img_cont_msg me-2">
                      <img
                        src={avatar1}
                        className="rounded-circle user_img_msg"
                        alt=""
                      />
                    </div>
                    <div
                      className={`msg_cotainer ${
                        item.status === "pending" ? "pending" : ""
                      }`}
                    >
                      {renderMessageContent(item)}
                      <br />
                      <span className="msg_time d-inline-flex align-items-center">
                        {convertTimestamp(item?.createdat)}
                        <span
                          className={`msg_status_icon ${
                            item.status === "sent" ? "sent" : "pending"
                          }`}
                          title={item.status === "sent" ? "Sent" : "Sending"}
                        >
                          <i className="fa fa-check" aria-hidden="true"></i>
                        </span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PerfectScrollbar>
      <div
        className="card-footer type_msg"
        style={{ padding: "15px", borderTop: "1px solid #e9ecef" }}
      >
        {!isVoiceMode ? (
          <div className="input-group d-flex gap-2">
            <textarea
              className="form-control"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              style={{
                border: "1px solid #ced4da",
                borderRadius: "5px",
                resize: "none",
                minHeight: "75px",
                maxHeight: "120px",
                padding: "10px",
              }}
              rows="1"
            />{" "}
            <button
              type="button"
              className="btn btn-outline-primary d-flex align-items-center justify-content-center "
              onClick={startVoiceRecording}
              title="Record voice message"
              style={{ borderRadius: "50%" }}
            >
              <i class="fa fa-microphone" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center justify-content-center"
              onClick={sendMessageHandler}
              disabled={!message.trim()}
              title="Send message"
              style={{ borderRadius: "50%" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
              </svg>
            </button>
          </div>
        ) : (
          <div
            className="voice-recorder rounded"
            style={{
              background: "#f8f9fa",
              padding: "20px",
              border: "1px solid #dee2e6",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "#667eea",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="white"
                    viewBox="0 0 16 16"
                  >
                    <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z" />
                    <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z" />
                  </svg>
                </div>
                <span className="fw-bold text-dark">
                  {isRecording ? "Recording..." : "Voice Message"}
                </span>
              </div>
              <button
                type="button"
                className="btn btn-link p-0 text-dark"
                onClick={cancelVoiceMode}
                title="Close"
                style={{ opacity: 0.6, fontSize: "24px", lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {isRecording && (
              <div
                className="d-flex justify-content-between align-items-center mb-3 p-3 rounded"
                style={{ background: "#fff", border: "1px solid #dee2e6" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="recording-pulse"
                    style={{
                      width: "12px",
                      height: "12px",
                      background: "#ef4444",
                      borderRadius: "50%",
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  ></div>
                  <span className="text-dark fw-medium">
                    Recording: {formatRecordingTime(recordingTime)}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center gap-1"
                  onClick={stopVoiceRecording}
                  style={{
                    borderRadius: "20px",
                    fontWeight: "500",
                    padding: "6px 16px",
                  }}
                >
                  <i
                    class="fa fa-square"
                    style={{ color: "red" }}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            )}

            {!isRecording && voicePreview && (
              <div
                className="p-3 rounded mb-3"
                style={{ background: "#fff", border: "1px solid #dee2e6" }}
              >
                <audio
                  controls
                  src={voicePreview.url}
                  className="w-100 mb-3"
                  style={{
                    height: "40px",
                    borderRadius: "8px",
                  }}
                >
                  Your browser does not support the audio element.
                </audio>
                <div className="d-flex gap-2 flex-wrap justify-content-center">
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={sendVoiceMessageHandler}
                    disabled={isSendingVoice}
                    style={{
                      borderRadius: "20px",
                      fontWeight: "500",
                      flex: "1",
                      minWidth: "120px",
                    }}
                  >
                    {isSendingVoice ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="me-1"
                          viewBox="0 0 16 16"
                        >
                          <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                        </svg>
                        Send
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3 d-flex justify-content-center align-items-center "
                    onClick={startVoiceRecording}
                    style={{ borderRadius: "20px", fontWeight: "500" }}
                  >
                    <i class="fa fa-refresh" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            )}

            {!isRecording && !voicePreview && (
              <div className="text-center py-3">
                <span
                  className={`d-block mb-3 ${
                    voiceError
                      ? "text-danger bg-white px-3 py-2 rounded border border-danger"
                      : "text-muted"
                  }`}
                  style={{ fontWeight: "500" }}
                >
                  {voiceError ? voiceError : "Initializing microphone..."}
                </span>
                <div className="d-flex gap-2 justify-content-center">
                  {voiceError && (
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={startVoiceRecording}
                      style={{ borderRadius: "20px", fontWeight: "500" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="me-1"
                        viewBox="0 0 16 16"
                      >
                        <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                        <path
                          fillRule="evenodd"
                          d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                        />
                      </svg>
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.1);
          }
        }
        
        .voice-message-bubble audio::-webkit-media-controls-panel {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        .voice-message-bubble audio::-webkit-media-controls-play-button,
        .voice-message-bubble audio::-webkit-media-controls-current-time-display,
        .voice-message-bubble audio::-webkit-media-controls-time-remaining-display {
          filter: invert(1);
        }
        
        .btn-outline-primary:hover {
          background-color: var(--bs-primary);
          color: white;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d;
          color: white;
        }
        
        .input-group textarea:focus {
          box-shadow: none;
          border-color: #ced4da;
        }
        
        .input-group button:focus {
          box-shadow: none;
        }

        .msg_cotainer,
        .msg_cotainer_send {
          max-width: 100%;
          word-wrap: break-word;
        }
        .msg_cotainer.pending,
        .msg_cotainer_send.pending {
          opacity: 0.6;
        }
        .msg_status_icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-left: 6px;
          font-size: 11px;
          line-height: 1;
        }
        .msg_status_icon.pending {
          color: #9aa0a6; /* gray */
        }
        .msg_status_icon.sent {
          color: #1a73e8; /* blue */
        }
      `}</style>
    </div>
  );
};

export default MsgBox;
